import {useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import {Button as BootstrapButton, Card, Col, ProgressBar, Row} from 'react-bootstrap';
import {DateTime} from 'luxon';
import {saveAs} from 'file-saver';
import {useIntl} from 'react-intl';
import TableMetronic from '../../../../app/common/components/table/TableMetronic';
import FilterSelect, {FilterOption} from '../../../../app/common/components/select/FilterSelect';
import Level3Title from '../../../../app/common/components/title/Level3Title';
import {useGetSuiviConsommationTelHD} from '../../Hooks/TelephonieManager';
import Loading from '../../../../app/common/components/Loading';

/**
 * Écran « Suivi de la consommation Tel HD ».
 *
 * Reprend l'écran de l'ancien espace client (voir DOCS/ECU_V2/img_2.png), vers
 * lequel cette page se contentait de rediriger jusqu'ici. Vue globale : agrège
 * toutes les prestations Téléphonie du client, pas une ligne en particulier.
 */

interface ConsommationVoIP {
  destination: string;
  duree: string;
  montant: string;
}

const PREMIER_MOIS_DISPONIBLE = DateTime.local(2014, 1, 1);

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/** `value` est la période au format API (`yyyy-MM`), `label` le libellé affiché. */
const MOIS: FilterOption<string>[] = (() => {
  const options: FilterOption<string>[] = [];
  for (
    let mois = DateTime.local().startOf('month');
    mois >= PREMIER_MOIS_DISPONIBLE;
    mois = mois.minus({months: 1})
  ) {
    options.push({
      value: mois.toFormat('yyyy-MM'),
      label: capitalize(mois.setLocale('fr').toFormat('MMMM yyyy')),
    });
  }
  return options;
})();

const SuiviConsommationTelHD = (): JSX.Element | null => {
  const intl = useIntl();
  const destinationLabel = intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION_HD.DESTINATION'});
  const dureeFactureeLabel = intl.formatMessage({
    id: 'TELEPHONIE.CONSOMMATION_HD.DUREE_FACTUREE',
  });
  const compteLabel = intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION_HD.COMPTE'});

  const CLASSEMENTS: FilterOption<string>[] = [destinationLabel, compteLabel].map((value) => ({
    value,
    label: value,
  }));

  const [mois, setMois] = useState<string>(MOIS[0].value);
  const [classement, setClassement] = useState<string>(CLASSEMENTS[0].value);
  const moisLabel = MOIS.find((option) => option.value === mois)?.label ?? mois;
  const classementApi = classement === compteLabel ? 'compte' : 'destination';
  const {data, isLoading} = useGetSuiviConsommationTelHD(mois, classementApi);

  const devise = data?.devise ?? 'EUR';

  const columns = useMemo<ColumnDef<ConsommationVoIP>[]>(
    () => [
      {header: destinationLabel, accessorKey: 'destination'},
      {header: dureeFactureeLabel, accessorKey: 'duree', size: 200},
      {header: devise, accessorKey: 'montant', size: 140},
    ],
    [destinationLabel, dureeFactureeLabel, devise]
  );

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  const {consommations, total, synthese, notePreselection} = data;

  const totalLabel = intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION_HD.TOTAL_LABEL'});

  const handleExportCsv = (): void => {
    const header = [destinationLabel, dureeFactureeLabel, devise];
    const rows = consommations.map((ligne) => [ligne.destination, ligne.duree, ligne.montant]);
    rows.push([totalLabel, total.duree, total.montant]);
    const csv = [header, ...rows].map((ligne) => ligne.join(';')).join('\n');
    const blob = new Blob([`﻿${csv}`], {type: 'text/csv;charset=utf-8'});
    saveAs(blob, `consommation-telephonique-${mois}.csv`);
  };

  const handleExportPdf = (): void => {
    window.print();
  };

  return (
    <>
      <Card className="mb-5">
        <Card.Header>
          <Card.Title className="m-0">
            <Level3Title
              title={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION_HD.DETAILED_TITLE'})}
            />
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-5 mb-5">
            <Col xs={12} md={6} className="d-flex align-items-center gap-4">
              <span className="fw-semibold fs-6" style={{minWidth: 60}}>
                {intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION_HD.MOIS_LABEL'})}
              </span>
              <FilterSelect options={MOIS} value={mois} onChange={setMois} />
            </Col>
            <Col xs={12} md={6} className="d-flex align-items-center gap-4">
              <span className="fw-semibold fs-6" style={{minWidth: 80}}>
                {intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION_HD.CLASSE_PAR_LABEL'})}
              </span>
              <FilterSelect options={CLASSEMENTS} value={classement} onChange={setClassement} />
            </Col>
          </Row>

          <p className="fs-6 mb-3">
            {intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION_HD.MONTHLY_SUMMARY_PREFIX'})}{' '}
            <strong>
              {intl.formatMessage(
                {id: 'TELEPHONIE.CONSOMMATION_HD.MONTHLY_SUMMARY_DETAIL'},
                {
                  montant: synthese.montant,
                  voix: synthese.voix,
                  data: synthese.data,
                  messages: synthese.messages,
                }
              )}
            </strong>
          </p>
          <ProgressBar
            now={synthese.pourcentage}
            variant="success"
            label={`${synthese.pourcentage.toLocaleString('fr-FR')} % (${synthese.montant} / ${
              synthese.plafond
            })`}
            style={{height: '22px'}}
          />
        </Card.Body>
      </Card>

      <TableMetronic<ConsommationVoIP>
        title={intl.formatMessage({id: 'TELEPHONIE.CONSOMMATION_HD.TABLE_TITLE'})}
        data={consommations}
        columns={columns}
        searchBar={false}
        stackedHeader
        caption={intl.formatMessage(
          {id: 'TELEPHONIE.CONSOMMATION_HD.TABLE_CAPTION'},
          {mois: moisLabel, classement: classement.toLowerCase()}
        )}
        toolbarEnd={
          <div className="d-flex gap-3">
            <BootstrapButton variant="outline-secondary" size="sm" onClick={handleExportCsv}>
              <i className="bi bi-filetype-csv me-2" />
              CSV
            </BootstrapButton>
            <BootstrapButton variant="outline-secondary" size="sm" onClick={handleExportPdf}>
              <i className="bi bi-filetype-pdf me-2" />
              PDF
            </BootstrapButton>
          </div>
        }
        elementName="destination"
        elementNamePlural="destinations"
      />

      {/* TableMetronic n'a pas de pied de tableau : le total est rendu à part
          pour ne pas devenir une ligne triable. */}
      <div className="d-flex flex-wrap gap-6 px-9 pb-6 fw-bold">
        <span>{totalLabel}</span>
        <span>
          <span className="text-muted me-2">{dureeFactureeLabel}</span>
          {total.duree}
        </span>
        <span>
          <span className="text-muted me-2">{devise}</span>
          {total.montant}
        </span>
      </div>

      <p className="text-muted fs-6">{notePreselection}</p>
    </>
  );
};

export default SuiviConsommationTelHD;
